"use client";

import Link from "next/link";
import { ArrowUpRight, Network, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { FORUM_SUBJECTS } from "@/lib/forum-subjects";
import { cn } from "@/lib/utils";
import type { SearchHit } from "@/lib/algolia";

type RecommendationTarget = "posts" | "problems";

type RecommendationState = {
  requestKey: string | null;
  status: "idle" | "ready" | "error";
  hits: SearchHit[];
};

type AlgoliaRecommendResponse = {
  hits?: SearchHit[];
};

type AlgoliaRecommendationsProps = {
  target: RecommendationTarget;
  objectID: string;
  title?: string;
  limit?: number;
  className?: string;
  hideWhenEmpty?: boolean;
  emptyMessage?: string;
};

const DEFAULT_LIMIT = 3;

// JCG-2471: Algolia usage limit으로 추천 결과가 정상 동작하지 않아 추천 기능(추천 문제·추천 개념)을 임시 비활성화.
// fetch 자체를 막아 Algolia usage를 아끼고, 섹션도 렌더하지 않는다. 재개 시 true로 변경.
const RECOMMENDATIONS_ENABLED = false;

const HTML_ENTITY_REPLACEMENTS: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
};
const PROBLEM_SCOPE_LABELS = new Map(
  FORUM_SUBJECTS.flatMap((subject) =>
    subject.scopes.map((scope) => [scope.key, scope.name])
  )
);
const PROBLEM_SUBJECT_LABELS = new Map(
  FORUM_SUBJECTS.map((subject) => [subject.slug, subject.name])
);

function getHitHref(hit: SearchHit): string | null {
  const href = hit.url || hit.pageUrl || (hit.slug ? `/${hit.slug}` : "");
  return href && href !== "#" ? href : null;
}

function getHitLabel(hit: SearchHit, target: RecommendationTarget): string {
  if (target === "problems") {
    return getProblemTypeLabel(hit);
  }

  return hit.category || hit.subject || "개념";
}

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&([a-z]+);/gi, (match, entity: string) => {
      return HTML_ENTITY_REPLACEMENTS[entity.toLowerCase()] ?? match;
    })
    .replace(/&#(\d+);/g, (match, code: string) => {
      const value = Number(code);
      return Number.isFinite(value) ? String.fromCharCode(value) : match;
    })
    .replace(/&#x([0-9a-f]+);/gi, (match, code: string) => {
      const value = Number.parseInt(code, 16);
      return Number.isFinite(value) ? String.fromCharCode(value) : match;
    });
}

function toPlainTextPreview(text: string): string {
  return decodeHtmlEntities(text)
    .replace(/\{\/\*[\s\S]*?\*\/\}/g, " ")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(
      /import\s+(?:type\s+)?(?:[\w$]+\s*(?:,\s*)?)?(?:\*\s+as\s+[\w$]+|\{[^{}]*\})?\s*from\s+['"][^'"\n]+['"]\s*;?/g,
      " "
    ) // 잔여 MDX import 구문 방어적 제거 (JCG-2352)
    .replace(/`([^`]+)`/g, "$1")
    .replace(/<[^>\n]+>/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[#*_~>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getPostTitle(hit: SearchHit): string {
  return hit.title || hit.objectID;
}

function getProblemTitle(hit: SearchHit): string {
  return toPlainTextPreview(hit.content || hit.title || "") || "문제 보기";
}

function getProblemTypeLabel(hit: SearchHit): string {
  if (hit.isFinal) return "파이널";
  if (hit.isHardcore) return "하드코어 11";
  if (hit.problemType) return `${hit.problemType} 문제`;
  if (hit.isCustom) return "연습문제";
  if (hit.isWritten) return "필기 기출";
  if (hit.isPractical) return "실기 기출";
  return "기출문제";
}

function getProblemScopeText(hit: SearchHit): string {
  return (
    (hit.scope ? PROBLEM_SCOPE_LABELS.get(hit.scope) : null) ||
    (hit.subject ? PROBLEM_SUBJECT_LABELS.get(hit.subject) : null) ||
    ""
  );
}

function getHitTitle(hit: SearchHit, target: RecommendationTarget): string {
  return target === "problems" ? getProblemTitle(hit) : getPostTitle(hit);
}

function getHitDescription(
  hit: SearchHit,
  target: RecommendationTarget
): string {
  if (target === "problems") {
    return getProblemScopeText(hit);
  }

  return toPlainTextPreview(hit.description || hit.content || "");
}

export default function AlgoliaRecommendations({
  target,
  objectID,
  title,
  limit = DEFAULT_LIMIT,
  className,
  hideWhenEmpty = true,
  emptyMessage,
}: AlgoliaRecommendationsProps) {
  const [state, setState] = useState<RecommendationState>({
    requestKey: null,
    status: "idle",
    hits: [],
  });
  const requestKey = `${target}:${objectID}:${limit}`;
  const isLoading = Boolean(objectID) && state.requestKey !== requestKey;
  const sectionTitle =
    title || (target === "problems" ? "추천 문제" : "추천 개념");
  const openInNewTab = target === "problems";
  const icon =
    target === "problems" ? (
      <Sparkles className="h-4 w-4" aria-hidden="true" />
    ) : (
      <Network className="h-4 w-4" aria-hidden="true" />
    );

  useEffect(() => {
    if (!objectID || !RECOMMENDATIONS_ENABLED) return;

    const controller = new AbortController();
    const params = new URLSearchParams({
      target,
      objectID,
      limit: String(limit),
    });

    fetch(`/api/algolia/recommend?${params.toString()}`, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (response) => {
        const body = (await response.json().catch(() => null)) as
          | AlgoliaRecommendResponse
          | null;
        if (!response.ok) {
          throw new Error("Algolia recommendation request failed");
        }
        return body?.hits ?? [];
      })
      .then((hits) => {
        const visibleHits = hits
          .filter((hit) => hit.objectID !== objectID && Boolean(getHitHref(hit)))
          .slice(0, limit);
        setState({ requestKey, status: "ready", hits: visibleHits });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (process.env.NODE_ENV !== "production") {
          console.warn("추천 결과를 불러오지 못했습니다.", error);
        }
        setState({ requestKey, status: "error", hits: [] });
      });

    return () => controller.abort();
  }, [target, objectID, limit, requestKey]);

  const items = useMemo(
    () =>
      state.hits.map((hit) => ({
        hit,
        href: getHitHref(hit),
        label: getHitLabel(hit, target),
        title: getHitTitle(hit, target),
        description: getHitDescription(hit, target),
      })),
    [state.hits, target]
  );

  if (!RECOMMENDATIONS_ENABLED) {
    return null;
  }
  if (
    !objectID ||
    (!isLoading && (state.status === "idle" || state.status === "error"))
  ) {
    return null;
  }
  if (
    !isLoading &&
    state.status === "ready" &&
    items.length === 0 &&
    hideWhenEmpty
  ) {
    return null;
  }

  return (
    <section
      aria-busy={isLoading}
      aria-label={sectionTitle}
      className={cn(
        "mt-8 mx-2 sm:mx-0 rounded-lg border border-border bg-card p-3 sm:p-4",
        className
      )}
    >
      <div className="mb-3 flex items-center gap-2 text-foreground">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
          {icon}
        </span>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold sm:text-lg">{sectionTitle}</h3>
          <span className="inline-flex items-center rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-semibold text-white dark:bg-amber-400 dark:text-amber-950">
            Beta
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-2 sm:grid-cols-3">
          {Array.from({ length: Math.min(limit, DEFAULT_LIMIT) }).map((_, i) => (
            <div
              key={i}
              className="h-[104px] rounded-md border border-border/70 bg-muted/40 p-3"
            >
              <div className="mb-3 h-3 w-16 rounded bg-muted-foreground/15" />
              <div className="mb-2 h-4 w-4/5 rounded bg-muted-foreground/15" />
              <div className="h-3 w-full rounded bg-muted-foreground/10" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-background px-3 py-6 text-center text-sm text-muted-foreground">
          {emptyMessage || "추천 항목이 아직 없습니다."}
        </div>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-3">
          {items.map(({ hit, href, label, title: itemTitle, description }) => {
            if (!href) return null;

            return (
              <li key={hit.objectID} className="min-w-0">
                <Link
                  href={href}
                  target={openInNewTab ? "_blank" : undefined}
                  rel={openInNewTab ? "noopener noreferrer" : undefined}
                  prefetch={false}
                  aria-label={
                    openInNewTab
                      ? `${itemTitle} 새 창에서 열기`
                      : undefined
                  }
                  className="group flex h-full min-h-[104px] flex-col rounded-md border border-border/80 bg-background px-3 py-2.5 text-left transition-colors hover:border-amber-300 hover:bg-amber-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:border-amber-700 dark:hover:bg-amber-950/25"
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-xs font-medium text-muted-foreground">
                      {label}
                    </span>
                    <ArrowUpRight
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      aria-hidden="true"
                    />
                  </div>
                  <span className="line-clamp-2 text-sm font-semibold leading-5 text-foreground">
                    {itemTitle}
                  </span>
                  {description && (
                    <span className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                      {description}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
